import LocalDate from '@booking-services/localDate'
import * as gtmDataLayer from './../analytics/gtm/dataLayer.js'
import clone from 'root-core/utils/object/clone.js'
import {pick, uniq} from 'underscore'
import * as aggregatePayments from '../../../../packages/aggregatePayments.js'
import fullBookingReference from '../../../../packages/bookings/fullBookingReference.js'
import BookingPriceBreakdown from './bookingPriceBreakdown.js'
import haveFlightsBeenRemovedByTropics from 'root-packages/airServices/haveFlightsBeenRemovedByTropics.js'
import hasPriceDiscrepancy from 'root-packages/price-engine/hasPriceDiscrepancy.js'

export default class Booking {
  constructor (
    {
      config,
      departure,
      api,
      quoteCriteria,
      showLoading,
      hideLoading,
      validateUser,
      datadog,
      user,
    } = {}
  ) {
    this.config = config
    this.datadog = datadog
    this.departure = departure
    this.api = api
    this.quoteCriteria = quoteCriteria
    this.booking = undefined
    this.showLoading = showLoading
    this.hideLoading = hideLoading
    this.validateUser = validateUser
    this.user = user
  }

  confirmBooking (confirmation) {
    if (!confirmation.confirmed) {
      var error = new Error(
        (confirmation.errors &&
         confirmation.errors.join('; '))
         || 'Sorry, your booking could not be confirmed')
      this.datadog.error('Booking could not be confirmed!', {error: error.message})
      throw error
    } else {
      this.datadog.info('Booking confirmed!')
      this.confirmation = confirmation
    }
  }

  complete () {
    return !!this.confirmation
  }

  toApiPriceOptions (priceOptions) {
    priceOptions.rooms.forEach(room => {
      room.passengers.forEach(p => {
        if (p.frequentTravellerDiscount) {
          p.pastPassengerDiscount = p.frequentTravellerDiscount
        }
      })
    })
    return priceOptions
  }

  toApiGuestBenefit (options) {
    return options.passengers
      .filter(p => p.pastGuestBenefit && p.pastGuestBenefit.selected)
      .map(p => `${p.title} ${p.firstName} ${p.lastName}`)
  }

  choosePaperDocuments () {
    const request = {
      bookingReference: this.booking.bookingReference,
    }

    return this.api
      .updatePaperDocs(request)
      .then(response => {
        return response
      })
  }

  changeRegistrationDetails () {
    const {leadPassenger, bookingReference} = this.booking
    const {emailAddress, mobileNumber: {internationalCode, number, numberWhileAway}} = leadPassenger

    const request = {
      email: emailAddress,
      bookingReference,
      mobilePhone: {
        countryCode: internationalCode,
        number,
        whileAway: (!!numberWhileAway).toString(),
      },
    }

    this.showLoading()
    return this.api
      .updateClientDetails(request)
      .then(response => {
        this.hideLoading()
        return response
      })
      .catch(() => {
        this.hideLoading()
      })
  }

  toApiReferrals ({passengers, referral}) {
    if(!referral?.referralCode && !referral?.advocateEmail) {
      return {}
    }
    const mapPassengers = passenger => {
      return {
        passengerId: passenger.passengerId,
        canApplyReferralSavings: !passenger?.pastGuestBenefit?.selected && !passenger?.frequentTravellerDiscount?.selected && !passenger.applyAdvocatePromo,
      }
    }

    return {
      ...referral,
      passengers: passengers.map(mapPassengers),
    }
  }

  async book () {
    const leadPassenger = clone(this.quoteCriteria.leadPassenger)
    const leadPassengerPhoneNumber = leadPassenger.phoneNumber


    const externalRef = this.quoteCriteria.ExternalRef
    const passengerCount = this.quoteCriteria.passengers.length

    const clientPrice = this.quoteCriteria.priceBreakdown().total

    const priceOptions = this.toApiPriceOptions(this.quoteCriteria.priceOptions())
    const referralData = this.toApiReferrals({passengers: this.quoteCriteria.passengers, referral: this.quoteCriteria.referral})

    const request = {
      sellingCompanyCode: this.departure.sellingCompanyCode,
      tourCode: this.departure.productCode,
      departureCode: this.departure.code,
      priceOptions,
      clientPrice,
      ExternalRef: externalRef,
      referral: referralData,
      pastGuestBenefit: this.toApiGuestBenefit({passengers: this.quoteCriteria.passengers}),
      membership: this.user.whitelabelOptions ? {
        membershipNo: this.user.whitelabelOptions.membershipNo,
        surname: this.user.whitelabelOptions.surname,
        membershipType: this.user.whitelabelOptions.membershipType,
        membershipTypeValue: this.user.whitelabelOptions.membershipTypeValue,
      } : {},
    }

    this.showLoading()
    this.datadog.info('Booking started...')
    const isUserValid = await this.validateUser(this.departure.sellingCompanyCode)

    if (!isUserValid) {
      this.datadog.warn('User validation failed!')
      throw {validationError: true}
    }

    return this.api
      .createBooking(request)
      .then(booking => {
        this.hideLoading()
        if (!booking.errors || booking.errors.length === 0) {
          this.booking = {
            bookingReference: booking.bookingReference,
            bookingId: fullBookingReference.parse(booking.bookingReference).bookingId,
            sellingCompanyCode: this.departure.sellingCompanyCode,
            leadPassenger: Object.assign(leadPassenger, {
              mobileNumber: {
                countryCode: leadPassengerPhoneNumber.countryCode,
                internationalCode: leadPassengerPhoneNumber.internationalCode,
              },
            }),
            externalRef: externalRef,
            passengerCount,
            totalAmount: booking.totalPrice,
            hotelsNotBooked: booking.hotelsNotBooked,
            flightsNotBooked: haveFlightsBeenRemovedByTropics({
              flightsNotBookedFlagFromTropicsResponse: booking.flightsNotBooked,
              didRequestAmadeusFlights: this.quoteCriteria.hasFlights(),
              didRequestPromisedFlights: this.quoteCriteria.hasPromisedFlights(),
              tropicsBookingHasAmadeusFlights: booking.hasOwnProperty('flights'),
              tropicsBookingHasPromisedFlights: booking.passengers.every(p => p.hasOwnProperty('promisedFlights')),
            }),
            tourOnRequest: booking.status === 'unconfirmedRequiresAction',
            creditCardTypes: uniq(booking.creditCardTypes.map(cardType => cardType.Code)),
            paymentMethods: this.paymentMethods(booking.paymentMethods),
            paymentOptions: this.paymentOptions(booking),
            priceDiscrepancy: hasPriceDiscrepancy({
              amountA: clientPrice,
              amountB: booking.totalPrice,
              tolerance: passengerCount / 100,
            }),
            priceBreakdown: this.getBookingPriceBrakedown({
              booking: booking,
              config: this.config,
            }),
          }

          this.datadog.updateBooking(this.booking)
          this.datadog.info('Booking was made!')
          return this.booking
        } else {
          this.responseErrors(booking.errors)
        }
      })
      .catch(error => {
        console.error('An error occured while making a booking', error, error.body)
        this.datadog.error('An error occured while making a booking', {
          error: error.body && error.body.errors || error,
        })
        this.hideLoading()
        if (error.body && error.body.errors) {
          this.responseErrors(error.body.errors, true)
        } else {
          throw error
        }
      })
  }

  getBookingPriceBrakedown (options) {
    return new BookingPriceBreakdown(options).getBookingPriceBreakdown()
  }

  responseErrors (errors, serverError) {
    const error = new Error('could not make booking')
    error.serverError = serverError
    error.errorMessages = errors.map(e => e.message || e)
    if (errors.find(e => e.property === 'departure' || e === 'The requested departure is either closed or cancelled.')) {
      this.departure.status = 'closed'
    }
    throw error
  }

  unload () {
    this.booking = undefined
  }

  totalAmount () {
    const breakdown = this.quoteCriteria.priceBreakdown()

    return {
      amount: this.booking?.totalAmount || breakdown.total,
      currency: this.departure.prices.currency,
    }
  }

  pushPaymentInformationToGtm (amount, type) {
    gtmDataLayer.push({
      currencyCode: this.departure.prices.currency,
      totalValue: this.booking.totalAmount,
      transactionType: type,
      noOfPassengers: this.booking.passengerCount,
      tourName: this.departure.name,
      isConfirmationPage: 'True',
      customerTown: this.booking.leadPassenger.address.city,
      customerProvince: this.booking.leadPassenger.address.region,
      customerCountry: this.booking.leadPassenger.address.country,
      externalRef: this.booking.externalRef,
      bookingId: this.booking.bookingId,
      userEmail: this.booking.leadPassenger.emailAddress,
    })
  }

  paymentTypeForGTM (amount) {
    return this.booking.paymentOptions.full &&
      amount >= this.booking.paymentOptions.full.amount
      ? 'Full'
      : 'Part'
  }

  payWithDirectDeposit (amount) {
    return this.pay(
      amount,
      'directDeposit'
    ).then(result => {
      result.directDeposit = true
    })
  }

  paidViaPaymentServices (amount, fee = 0) {
    this.pushPaymentInformationToGtm(amount, this.paymentTypeForGTM(amount))
    this.confirmBooking({
      confirmed: true,
      confirmationEmailAddress: this.booking.leadPassenger.emailAddress,
      creditCardPayment: {
        amount,
        fee,
        currency: this.departure.prices.currency,
        hideAmountPaid: this.paidViaUplift,
      },
    })
  }

  paidViaZipMoney (options) {
    this.pushPaymentInformationToGtm(options.amount, this.paymentTypeForGTM(options.amount))
    this.confirmBooking({
      confirmed: true,
      confirmationEmailAddress: this.booking.leadPassenger.emailAddress,
      zipMoney: {onHold: false, reference: options.reference},
    })
  }

  paidViaPayPal (paypalPayment) {
    this.confirmBooking({
      confirmed: true,
      confirmationEmailAddress: this.booking.leadPassenger.emailAddress,
      paypalPayment,
    })
  }

  paidViaPaymentGateway (paymentGatewayConfirmation) {
    const totalAmountPaid = Number(paymentGatewayConfirmation.paymentDetails.amount.total || 0)
    const feeAmountPaid = Number(paymentGatewayConfirmation.paymentDetails.amount.fee || 0)
    this.pushPaymentInformationToGtm(totalAmountPaid, this.paymentTypeForGTM(totalAmountPaid))
    this.confirmBooking({
      confirmed: true,
      confirmationEmailAddress: this.booking.leadPassenger.emailAddress,
      paymentGateway: {
        amount: totalAmountPaid,
        fee: feeAmountPaid,
        currency: this.departure.prices.currency,
        paymentMethod: paymentGatewayConfirmation.paymentDetails.paymentMethod,
      },
    })
    this.api.sendConfirmationEmail(this.booking.bookingReference)
  }

  payWithCheque (amount, reference) {
    return this.pay(
      amount,
      'cheque',
      reference
    ).then(result => {
      result.cheque = true
    })
  }

  payWithMCOWRA (amount, reference) {
    return this.pay(amount, 'mcowra', reference).then(result => {
      result.mcowra = true
    })
  }

  payWithPaymentGate (amount) {
    return this.pay(amount, 'paymentGate', '').then(result => {
      result.paymentGate = true
    })
  }

  payWithElectronicFundsTransfer (amount) {
    return this.pay(
      amount,
      'electronicFundsTransfer',
      ''
    ).then(result => {
      result.electronicFundsTransfer = true
    })
  }

  payWithEnett (amount) {
    return this.pay(amount, 'eNett').then(result => {
      result.eNett = true
    })
  }

  payWithSmartMoney (amount) {
    return this.pay(amount, 'smartMoney').then(result => {
      result.smartMoney = true
    })
  }

  payWithFakeMethod (amount) {
    return this.pay(amount, 'fake').then(result => {
      result.fakePayment = {
        amount,
      }
    })
  }

  pay (amount, paymentMethod, reference) {
    var paymentDetails = pick({
      bookingReference: this.booking.bookingReference,
      paymentCurrencyCode: this.departure.prices.currency,
      paymentAmount: amount,
      paymentMethod: paymentMethod,
      paymentReference: reference,
    }, property => property !== undefined)

    this.showLoading()
    return this.api
      .pay(paymentDetails)
      .then(result => {
        this.hideLoading()
        this.pushPaymentInformationToGtm(amount, this.paymentTypeForGTM(amount))
        result[paymentMethod] = true
        result.confirmed = true
        this.confirmBooking(result)
        return result
      })
      .catch(error => {
        this.hideLoading()
        throw error.body || error
      })
  }

  payWithCreditAgreement (amount) {
    return this.pay(amount, 'creditAgreement', '').then(result => {
      result.creditAgreementPayment = true
    })
  }

  placeBookingOnHold () {
    const paymentDetails = {bookingReference: this.booking.bookingReference}
    this.showLoading()
    return this.api
      .hold(paymentDetails)
      .then(result => {
        this.hideLoading()
        this.pushPaymentInformationToGtm(paymentDetails.paymentAmount, 'Hold')
        result.onHold = {
          dueDate: this.booking.paymentOptions.placeBookingOnHold.dueDate,
        }
        result.confirmed = true
        this.confirmBooking(result)
      })
      .catch(error => {
        this.hideLoading()
        throw error.body || error
      })
  }

  zipMoneyReferralHold (reference) {
    const paymentDetails = {bookingReference: this.booking.bookingReference}
    this.showLoading()
    return this.api
      .hold(paymentDetails)
      .then(result => {
        this.hideLoading()
        this.pushPaymentInformationToGtm(paymentDetails.paymentAmount, 'Hold')
        result.zipMoney = {onHold: true, reference: reference},
        result.confirmed = true
        this.confirmBooking(result)
      })
      .catch(error => {
        this.hideLoading()
        throw error.body || error
      })
  }

  acceptTourOnRequest () {
    const request = {
      bookingReference: this.booking.bookingReference,
      sellingCompanyCode: this.departure.sellingCompanyCode,
      tourCode: this.departure.productCode,
      departureCode: this.departure.code,
    }

    this.showLoading()
    return this.api
      .request(request)
      .then(result => {
        this.hideLoading()
        result.tourOnRequest = true
        result.confirmed = true
        this.confirmBooking(result)
        if (result.confirmed) {
          gtmDataLayer.push({
            event: 'userBookingWarning',
            userBookingWarning: 'Booking on request',
          })

          this.pushPaymentInformationToGtm(this.booking.totalAmount, 'Hold')
        }
      })
      .catch(error => {
        console.error(error)
        this.hideLoading()
        throw error
      })
  }

  paymentMethods (methods) {
    return {
      creditCard: methods.indexOf('creditCard') != -1,
      directDeposit: methods.indexOf('directDeposit') != -1,
      cheque: methods.indexOf('cheque') != -1,
      mcowra: methods.indexOf('mcowra') != -1,
      electronicFundsTransfer: methods.indexOf('electronicFundsTransfer') != -1,
      eNett: methods.indexOf('eNett') != -1,
      smartMoney: methods.indexOf('smartMoney') != -1,
      creditAgreementPayment: methods.indexOf('creditAgreement') != -1,
      paymentGate: methods.indexOf('paymentGate') != -1,
    }
  }

  paymentOptions (booking) {
    const aggregatedDeposit = aggregatePayments.aggregateDeposit(booking.paymentSchedule)
    return {
      placeBookingOnHold: this.getPlaceBookingOnHoldOption({autoCancellationDate: booking.onHoldEndDate, aggregatedDeposit}),
      deposit: aggregatedDeposit,
      full: aggregatePayments.aggregateFullPayment(booking),
    }
  }

  calculateOnHoldDueDate (depositDueDate, autoCancellationDate) {
    const onHoldAutoCancellationDate = new LocalDate(autoCancellationDate)
    if (depositDueDate) {
      const onHoldDepositDueDate = new LocalDate(depositDueDate)
      return (onHoldDepositDueDate < onHoldAutoCancellationDate)
        ? onHoldDepositDueDate : onHoldAutoCancellationDate
    }
    return onHoldAutoCancellationDate
  }

  getPlaceBookingOnHoldOption ({autoCancellationDate, aggregatedDeposit}) {
    if (new LocalDate(autoCancellationDate) > new LocalDate()) {
      const depositDueDate = aggregatedDeposit && aggregatedDeposit.dueDate
      return {
        depositDueDate,
        autoCancellationDate,
        dueDate: this.calculateOnHoldDueDate(depositDueDate, autoCancellationDate),
      }
    }
  }
}
